import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlatList, HStack, View } from 'native-base';

import { Text } from '@pimm/base';
import { LAYOUT } from '../consts';
import { SalesDaypart } from '../types';
import { useSalesLaborTracker } from '../context/sales-labor-tracker.context';
import { type SalesRow, SalesTableRow } from './sales-table-row';
import { TimeSlots } from './time-slots';

// Manager view: combined Actual/Forecast row + Cumulative Variance
// Detail view: Actual, Forecast, Variance (Actual to Forecast), Variance (Cumulative)
const SALES_ROWS: SalesRow[] = [
  {
    key: 'actual_forecast',
    label: 'actual_forecast',
    getValue: slot => (slot.hasActual ? slot.salesActual : slot.salesForecast),
    showInManager: true,
    isManagerCombined: true,
  },
  {
    key: 'actual',
    label: 'actual',
    getValue: slot => slot.salesActual,
  },
  {
    key: 'forecast',
    label: 'forecast',
    getValue: slot => slot.salesForecast,
    isForecastData: true,
  },
  {
    key: 'variance_actual',
    label: 'variance_sales_actual',
    getValue: slot => slot.salesVar,
    isVariance: true,
    isBold: true,
    isForecastData: true,
  },
  {
    key: 'variance_cumulative',
    label: 'variance_cumulative',
    getValue: slot => slot.salesCumVar,
    showInManager: true,
    isBold: true,
    isVariance: true,
  },
];

type SalesTableProps = {
  daypart?: Partial<SalesDaypart>;
  isDetailed?: boolean;
};

export const SalesTable = ({ daypart, isDetailed }: SalesTableProps) => {
  const { t } = useTranslation();
  const displayForecastedSales = useSalesLaborTracker(_ => _.displayForecastedSales);
  const rows = useMemo(() => {
    return SALES_ROWS.filter(_ => {
      if (_.isForecastData && !displayForecastedSales) return false;
      if (!isDetailed) return _.showInManager;
      return !_.isManagerCombined;
    });
  }, [daypart, displayForecastedSales, isDetailed]);

  return (
    <View w="full" rounded={10} borderWidth={1} overflow="hidden">
      {/* Column headers */}
      <HStack bg="coolGray.50" borderBottomWidth={1}>
        <HStack alignItems="center" px={2.5} py={1} w={LAYOUT.stickyLabelW} minH={6} borderRightWidth={1}>
          <Text size="sm" fontWeight={700} color="black" lineHeight="xs">
            {t('sales_tracker:sales')}
            {'  '}
            <Text size="xs" fontWeight={400} color="gray.600" lineHeight="xs">
              {t('sales_tracker:shaded')}
            </Text>
          </Text>
        </HStack>
        <TimeSlots tableData={daypart} />
      </HStack>

      {/* Data rows */}
      <FlatList
        bg="white"
        data={rows}
        scrollEnabled={false}
        keyExtractor={item => item.key}
        ItemSeparatorComponent={() => <Box h="1px" bg="gray.100" />}
        renderItem={({ item: row }) => <SalesTableRow row={row} tableData={daypart} displayForecastedSales={displayForecastedSales} />}
      />
    </View>
  );
};
